import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';
import { userTypes } from '../../../../Shared/_constants/constants';
import { STAFF_ROLES } from '../staff-roles';
import { SetPasswordComponent } from '../../../../Shared/_components/set-password/set-password.component';
import { GRANTABLE_PAGES, PAGES, PAGE_GROUPS, PageDef, offerablePages, rolePages, SUPER_ADMIN_ONLY_PAGES } from '../../../../Shared/_constants/page-access';

@Component({
    selector: 'app-staff-dialog',
    imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, SetPasswordComponent],
    templateUrl: './staff-dialog.component.html'
})
export class StaffDialogComponent implements OnInit {
  isEdit = false;
  saving = false;
  email = '';
  firstname = '';
  lastname = '';
  selected = new Set<string>();
  isActive = true;
  isSuperAdmin = false;
  // Someone who is already a super admin can only be changed by a super admin.
  lockedBySuper = false;

  readonly roleOptions = STAFF_ROLES;

  // Page access: by default a person sees whatever their roles allow. A super
  // admin can instead pick exactly which of those pages they get.
  customAccess = false;
  pages = new Set<string>();

  // Works as a dialog or inside the staff edit page: there it gets the person
  // through `pageData` and reports back through `done`.
  @Input() pageData: { member?: any } | null = null;
  @Output() done = new EventEmitter<{ status: boolean }>();
  private loaded = '';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { member?: any } | null,
    @Optional() private dialogRef: MatDialogRef<StaffDialogComponent> | null,
    private userService: UserService,
    private alertService: AlertService,
  ) {}

  get data(): { member?: any } {
    return this.pageData ?? this.dialogData ?? {};
  }

  get pageMode(): boolean {
    return !this.dialogRef;
  }

  private get snapshot(): string {
    return JSON.stringify([this.email, this.firstname, this.lastname, [...this.selected].sort(), this.isActive, this.customAccess, [...this.pages].sort()]);
  }

  get hasChanges(): boolean {
    return !!this.loaded && this.snapshot !== this.loaded;
  }

  private close(result: { status: boolean }): void {
    if (this.dialogRef) this.dialogRef.close(result);
    else {
      if (result.status) this.loaded = this.snapshot;
      this.done.emit(result);
    }
  }

  ngOnInit(): void {
    this.isSuperAdmin = this.userService.isSuperAdmin();
    setTimeout(() => (this.loaded = this.snapshot));
    const m = this.data?.member;
    if (m) {
      this.isEdit = true;
      this.email = m.useremail;
      this.firstname = m.firstname || '';
      this.lastname = m.lastname || '';
      this.selected = new Set(m.roles);
      if (Array.isArray(m.allowedPages)) {
        this.customAccess = true;
        this.pages = new Set(m.allowedPages);
      }
      this.isActive = m.isActive !== false;
      this.lockedBySuper = m.roles.includes('S-ADMIN') && !this.isSuperAdmin;
    }
  }

  // S-ADMIN can only be granted by a super admin.
  isDisabled(role: { value: string; superOnly?: boolean }): boolean {
    return this.lockedBySuper || (!!role.superOnly && !this.isSuperAdmin);
  }

  toggle(role: string): void {
    if (this.selected.has(role)) this.selected.delete(role);
    else this.selected.add(role);
  }

  // ---- Page access ----
  get isSuperRole(): boolean {
    return this.selected.has('S-ADMIN');
  }

  // Pages the picker offers: what the chosen roles reach, plus the extras a
  // super admin can hand out on top (the dashboard).
  get reachable(): Set<string> {
    return offerablePages([...this.selected]);
  }

  // An extra the roles don't give on their own - only given when ticked.
  isExtra(key: string): boolean {
    return GRANTABLE_PAGES.has(key) && !rolePages([...this.selected]).has(key);
  }

  get groups(): { name: string; pages: PageDef[] }[] {
    const reach = this.reachable;
    return PAGE_GROUPS.map((name) => ({ name, pages: PAGES.filter((p) => p.group === name && reach.has(p.key)) })).filter((g) => g.pages.length);
  }

  get pagesLocked(): number {
    // Owner-only pages aren't the roles' to give, so they don't count as locked.
    return PAGES.filter((p) => !SUPER_ADMIN_ONLY_PAGES.has(p.key)).length - this.reachable.size;
  }

  get chosenCount(): number {
    const reach = this.reachable;
    return [...this.pages].filter((k) => reach.has(k)).length;
  }

  setCustom(on: boolean): void {
    this.customAccess = on;
    // Start from everything they can reach, then untick what they shouldn't have.
    if (on && this.chosenCount === 0) this.pages = new Set(this.reachable);
  }

  togglePage(key: string): void {
    if (this.pages.has(key)) this.pages.delete(key);
    else this.pages.add(key);
  }

  groupState(g: { pages: PageDef[] }): 'all' | 'some' | 'none' {
    const n = g.pages.filter((p) => this.pages.has(p.key)).length;
    return n === 0 ? 'none' : n === g.pages.length ? 'all' : 'some';
  }

  toggleGroup(g: { pages: PageDef[] }): void {
    const all = this.groupState(g) === 'all';
    g.pages.forEach((p) => (all ? this.pages.delete(p.key) : this.pages.add(p.key)));
  }

  selectAllPages(): void {
    this.pages = new Set(this.reachable);
  }

  clearPages(): void {
    this.pages = new Set();
  }

  onSave(): void {
    if (!this.isEdit && !/^\S+@\S+\.\S+$/.test(this.email.trim())) {
      this.alertService.alert('Enter a valid email address', 'error');
      return;
    }
    if (this.firstname.trim().length < 2 || this.lastname.trim().length < 2) {
      this.alertService.alert('Enter a first and last name', 'error');
      return;
    }
    if (this.selected.size === 0) {
      this.alertService.alert('Choose at least one role', 'error');
      return;
    }
    // null = follow the roles; a list = only these pages (and only ones the roles can reach).
    let allowedPages: string[] | null = null;
    if (this.customAccess && !this.isSuperRole) {
      const reach = this.reachable;
      allowedPages = PAGES.map((p) => p.key).filter((k) => this.pages.has(k) && reach.has(k));
      if (allowedPages.length === 0) {
        this.alertService.alert('Choose at least one page, or switch back to "Everything their roles allow"', 'error');
        return;
      }
    }
    // Keep the role order stable (highest first) so stored values look the same.
    const userrole = this.roleOptions.map((r) => r.value).filter((v) => this.selected.has(v)).join(',');
    this.saving = true;

    const request = this.isEdit
      ? this.userService.updateUser({
          id: this.data.member.userid,
          firstname: this.firstname.trim(),
          lastname: this.lastname.trim(),
          usertype: userTypes.NONMEMBER,
          userrole,
          isActive: this.isActive,
          allowedPages,
        })
      : this.userService.createUserWithRole({
          useremail: this.email.trim().toLowerCase(),
          userrole,
          usertype: userTypes.NONMEMBER,
          firstname: this.firstname.trim(),
          lastname: this.lastname.trim(),
          subscribe: false,
        });

    request.subscribe({
      next: (created: any) => {
        // A new account is created first, then given its page list.
        if (!this.isEdit && allowedPages && created?.userid) {
          this.userService
            .updateUser({
              id: created.userid,
              firstname: this.firstname.trim(),
              lastname: this.lastname.trim(),
              usertype: userTypes.NONMEMBER,
              userrole,
              allowedPages,
            })
            .subscribe({
              next: () => this.finish(),
              error: (error) => {
                this.saving = false;
                this.alertService.alert('Created, but page access was not saved: ' + (error?.error?.message || 'try editing them'), 'error');
                this.close({ status: true });
              },
            });
          return;
        }
        this.finish();
      },
      error: (error) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not save', 'error');
      },
    });
  }

  private finish(): void {
    this.saving = false;
    this.userService.clearAccessCache();
    this.alertService.alert(this.isEdit ? 'Staff member updated' : 'Staff member created', 'success');
    this.close({ status: true });
  }

  onCancel(): void {
    this.close({ status: false });
  }
}
